import React,{useEffect,useRef,useState} from 'react';
import {FiCheck,FiMessageCircle,FiTrash2} from 'react-icons/fi';
import supabase from '../supabase/supabase';
import {reviewRpc} from '../lib/clientReview';

export default function StoryCommentsPanel({projectId,focus,onClear,onData,onNavigate,activity=0,notify=()=>{},disabled=false,openCommentId=null,compact=false,blockId=null,onCreate}){
 const [list,setList]=useState({comments:[],replies:[]});
 const [draft,setDraft]=useState(''),[replyDrafts,setReplyDrafts]=useState({});
 const [busy,setBusy]=useState(false),[error,setError]=useState(''),[message,setMessage]=useState('');
 const [expanded,setExpanded]=useState(openCommentId),[filter,setFilter]=useState('open');
 const [confirmDeleteCommentId,setConfirmDeleteCommentId]=useState(null);
 const input=useRef(null),pending=useRef(null),mounted=useRef(true),autoOpened=useRef(null);
 const call=(action,payload={})=>reviewRpc(supabase,'cutscript_story_comment_owner',{p_project:projectId,p_action:action,p_payload:payload});
 const refresh=async()=>{
  if(!projectId)return;
  try{
   const next=await call('list');
   if(mounted.current){
    setList(next);onData?.(next.comments||[]);
    if(compact && blockId && autoOpened.current!==blockId){
      autoOpened.current=blockId;
      const passageNotes=(next.comments||[]).filter(note=>note.block_id===blockId)
        .sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
      const latest=passageNotes.find(note=>note.id===openCommentId)
        ||passageNotes.find(note=>note.status==='open')||passageNotes[0];
      if(latest){setExpanded(latest.id);onNavigate?.(latest);}
    }
   }
  }catch(e){if(mounted.current)setError(e.message);}
 };
 useEffect(()=>{mounted.current=true;refresh();return()=>{mounted.current=false;};},[projectId,activity]);
 useEffect(()=>{if(focus){setDraft('');setError('');requestAnimationFrame(()=>input.current?.focus());}},[focus?.blockId,focus?.start,focus?.end]);
 useEffect(()=>{if(openCommentId)setExpanded(openCommentId);},[openCommentId]);
 const save=async(e)=>{
  e.preventDefault();
  const body=draft.trim();
  if(!focus||!body||busy||disabled)return;
  setBusy(true);setError('');setMessage('');
  try{
   const key=JSON.stringify({focus,body});
   if(pending.current?.key!==key)pending.current={id:crypto.randomUUID(),key};
   const addedId=pending.current.id;
   await call('add',{id:addedId,blockId:focus.blockId,start:focus.start,end:focus.end,body});
   setExpanded(addedId);onNavigate?.({id:addedId,block_id:focus.blockId});
   pending.current=null;setDraft('');onClear?.();setMessage('Comment attached to this passage.');
   await refresh();notify();
  }catch(ex){setError(ex.message||'Could not save comment. Your draft is retained.');}
  finally{setBusy(false);}
 };
 const act=async(comment,action)=>{
  if(busy||disabled)return;
  setBusy(true);setError('');setMessage('');
  try{
   const body=replyDrafts[comment.id]?.trim(),payload={commentId:comment.id};
   if(action==='reply'){if(!body)return;payload.body=body;payload.id=crypto.randomUUID();}
   await call(action,payload);
   if(action==='reply')setReplyDrafts(d=>({...d,[comment.id]:''}));
   if(action==='delete'){setExpanded(null);setConfirmDeleteCommentId(null);}
   setMessage(action==='delete'?'Comment deleted.':action==='reply'?'Reply added.':action==='resolve'?'Discussion resolved.':'Discussion reopened.');
   await refresh();notify();
  }catch(ex){setError(ex.message);}
  finally{setBusy(false);}
 };
 const all=(list.comments||[]).filter(c=>!blockId||c.block_id===blockId),filtered=compact?all:filter==='all'?all:all.filter(c=>filter==='open'?c.status==='open':c.status==='resolved');
 return <div className="story-discussions" aria-label="Assembly comments">
  {!compact&&<div className="story-discussion-intro"><h3><FiMessageCircle/> Assembly comments <span>{all.filter(c=>c.status==='open').length} open</span></h3><p>Highlight a sentence in <strong>Assembly</strong> and choose Comment. Notes follow the passage when you reorder it.</p></div>}
  {compact&&!focus&&<button type="button" className="story-margin-add" onClick={onCreate}><FiMessageCircle/> New comment on passage</button>}
  {focus&&<form className="story-comment-compose" onSubmit={save}>
    <div className="story-compose-heading"><strong>New comment</strong><button type="button" onClick={onClear} title="Cancel commenting">×</button></div>
    <small>Passage · {focus.sourceTitle||'Assembly'}</small>
    <blockquote>{focus.quote}</blockquote>
    <label htmlFor="story-comment-body">Your comment</label>
    <textarea ref={input} id="story-comment-body" rows={3} maxLength={2000} placeholder="What needs attention in this passage?" value={draft} disabled={busy||disabled} onChange={e=>setDraft(e.target.value)}/>
    <div className="story-compose-footer"><span>Private editor note. Client reviews remain separate.</span><button type="submit" className="story-comment-submit" disabled={busy||disabled||!draft.trim()}>{busy?'Saving…':'Post comment'}</button></div>
  </form>}
  {!compact&&<div className="story-comment-filters" role="group" aria-label="Filter story discussions">
   {['open','resolved','all'].map(v=><button key={v} aria-pressed={filter===v} onClick={()=>setFilter(v)}>{v==='open'?'Open':v==='resolved'?'Resolved':'All'}</button>)}
  </div>}
  {error&&<p className="feedback-error" role="alert">{error}</p>}
  {message&&<p className="feedback-message" role="status"><FiCheck/> {message}</p>}
  {!filtered.length&&<div className="feedback-empty"><FiMessageCircle/><h3>{all.length?'No comments in this filter':'No comments on this passage'}</h3><p>Highlight words and select Comment, or use the passage icon to leave a note.</p></div>}
  <div className="story-comment-list">{filtered.map(c=>{
   const open=expanded===c.id,thread=(list.replies||[]).filter(r=>r.comment_id===c.id);
   return <article key={c.id} className={'story-thread-card'+(open?' is-expanded':'')}>
    <button className="story-thread-toggle" aria-expanded={open} onClick={()=>{setExpanded(open?null:c.id);onNavigate?.(open?null:c);}}>
     <span className="story-thread-top"><strong>{c.author_kind==='owner'?'You (editor)':c.guest_name}</strong><small>{c.status==='resolved'?'Resolved':'Open'} · {new Date(c.created_at).toLocaleDateString()}</small></span>
     <span className="comment-quote-label-static">Quoted passage</span>
     <span className="story-thread-quote"><em>“{c.quote}”</em></span>
     <span className="comment-body-label">Comment</span>
     <span className="story-thread-body"><strong>{c.body}</strong></span>
     {!!thread.length&&<small>{thread.length} {thread.length===1?'reply':'replies'}</small>}
    </button>
    {c.author_kind==='owner'&&<button type="button" className="story-thread-delete" disabled={busy||disabled}
      title="Delete this comment" aria-label="Delete this comment"
      aria-expanded={confirmDeleteCommentId===c.id}
      onClick={()=>setConfirmDeleteCommentId(current=>current===c.id?null:c.id)}>
      <FiTrash2 aria-hidden="true"/>
    </button>}
    {confirmDeleteCommentId===c.id&&<div className="comment-inline-confirm" role="group" aria-label="Confirm comment deletion">
      <strong>Delete this comment?</strong>
      <p>The comment and its replies will be removed. This can't be undone.</p>
      <div className="comment-inline-confirm-actions">
        <button type="button" disabled={busy||disabled} onClick={()=>setConfirmDeleteCommentId(null)}>Cancel</button>
        <button type="button" className="comment-inline-delete" disabled={busy||disabled} onClick={()=>act(c,'delete')}>{busy?'Deleting…':'Delete comment'}</button>
      </div>
    </div>}
    {open&&<div className="story-thread-expanded">
     {thread.map(r=><div className="feedback-reply" key={r.id}><strong>{r.author_kind==='owner'?'You (editor)':c.guest_name}</strong><p>{r.body}</p></div>)}
     <form onSubmit={e=>{e.preventDefault();act(c,'reply');}}><label htmlFor={'story-reply-'+c.id}>Reply</label><textarea id={'story-reply-'+c.id} rows={2} maxLength={2000} disabled={busy||disabled} placeholder="Write a reply…" value={replyDrafts[c.id]||''} onChange={e=>setReplyDrafts(d=>({...d,[c.id]:e.target.value}))}/><div className="story-thread-controls"><button type="submit" disabled={busy||disabled||!replyDrafts[c.id]?.trim()}>Reply</button><button type="button" disabled={busy||disabled} onClick={()=>act(c,c.status==='open'?'resolve':'reopen')}>{c.status==='open'?'Resolve':'Reopen'}</button>
      </div></form>
    </div>}
   </article>;
  })}</div>
 </div>;
}
